import {makeApiCall} from '../core';
import type {
  BundlePriceResult,
  BundlePricingPolicy,
  CustomerMetrics,
  ItemEnvelope,
  ListEnvelope,
  ServiceBundle,
} from '@ruta/shared';

// =====================
// Service Bundles API
// =====================
//
// These wrappers unwrap the standard response envelopes so callers receive
// the resource directly and never read an envelope key themselves.

export interface CreateServiceBundleData {
  name: string;
  description?: string;
  pricingPolicy: BundlePricingPolicy;
  serviceTemplateIds: string[]; // 2+ unique ids
}

export interface UpdateServiceBundleData {
  name?: string;
  description?: string | null; // null clears the description
  pricingPolicy?: BundlePricingPolicy;
  serviceTemplateIds?: string[]; // full replacement, 2+ unique ids
  isActive?: boolean;
}

export interface CalculateBundlePriceParams {
  squareFootage?: number;
  customerMetrics?: CustomerMetrics;
  budgetedHoursByTemplateId?: Record<string, number>;
  /** Preview an in-flight policy before saving it. */
  pricingPolicy?: BundlePricingPolicy;
}

export const getServiceBundles = async (
  options: { includeInactive?: boolean } = {},
): Promise<ServiceBundle[]> => {
  const query = options.includeInactive ? '?includeInactive=true' : '';
  const res = await makeApiCall<ListEnvelope<ServiceBundle>>('GET', `/service-bundles${query}`);
  return res.data;
};

export const getServiceBundle = async (bundleId: string): Promise<ServiceBundle> => {
  const res = await makeApiCall<ItemEnvelope<ServiceBundle>>('GET', `/service-bundles/${bundleId}`);
  return res.data;
};

export const createServiceBundle = async (
  data: CreateServiceBundleData,
): Promise<ServiceBundle> => {
  const res = await makeApiCall<ItemEnvelope<ServiceBundle>>('POST', '/service-bundles', data);
  return res.data;
};

export const updateServiceBundle = async (
  bundleId: string,
  data: UpdateServiceBundleData,
): Promise<ServiceBundle> => {
  const res = await makeApiCall<ItemEnvelope<ServiceBundle>>('PATCH', `/service-bundles/${bundleId}`, data);
  return res.data;
};

export const deactivateServiceBundle = async (
  bundleId: string,
): Promise<{ id: string; name: string; isActive: boolean; updatedAt: string }> => {
  const res = await makeApiCall<ItemEnvelope<{ id: string; name: string; isActive: boolean; updatedAt: string }>>(
    'DELETE',
    `/service-bundles/${bundleId}`,
  );
  return res.data;
};

export const calculateBundlePrice = async (
  bundleId: string,
  params: CalculateBundlePriceParams,
): Promise<BundlePriceResult> => {
  const res = await makeApiCall<ItemEnvelope<BundlePriceResult>>(
    'POST',
    `/service-bundles/${bundleId}/price`,
    params,
  );
  return res.data;
};

/** Result of subscribing a customer to a bundle (RUT-2210). */
export interface SubscribeToBundleResult {
  bundleId: string;
  /** Subscriptions newly created for the bundle (one per not-yet-subscribed service). */
  created: unknown[];
  /** Service-template ids the customer already had, brought into the bundle (RUT-2211). */
  adopted: string[];
  /** Outcome of the single bundle charge (RUT-2211). */
  charge: { status: string; amount?: number; paymentId?: string };
}

export interface SubscribeToBundleOptions {
  /** Book the package price as a balance owed instead of charging it now, so a
   * payment plan can collect it in installments (RUT-2493). */
  usePaymentPlan?: boolean;
  /** The member rates quoted to this customer, keyed by template id (RUT-3610).
   * A derived package total follows these; each member keeps its quoted rate. */
  memberRates?: Record<string, number>;
}

/** Activate a package on a customer: creates or attaches the members and collects one package total. */
export const subscribeCustomerToBundle = async (
  customerId: string,
  bundleId: string,
  options: SubscribeToBundleOptions = {},
): Promise<SubscribeToBundleResult> => {
  const res = await makeApiCall<ItemEnvelope<SubscribeToBundleResult>>(
    'POST',
    `/customers/${customerId}/bundles`,
    {
      bundleId,
      usePaymentPlan: options.usePaymentPlan,
      memberRates: options.memberRates,
    },
  );
  return res.data;
};
